import classNames from 'classnames';
import { type ComponentProps, type ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import Logo from '@alltrails/denali/components/Logo';
import { ModalAriaLabelledByContext } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import AltLoginsForm from '../AltLoginsForm';
import SectionDivider from '../SectionDivider';
import TermsOfService from '../TermsOfService';
import styles from './SignUpModal.module.scss';

type InitialProps = Pick<ComponentProps<typeof AltLoginsForm>, 'appleLoginHandler' | 'facebookLoginHandler' | 'googleLoginHandler'> & {
  deeplinkData?: DeeplinkData;
  displayTrigger?: CarouselDisplayTrigger;
  heading?: ReactNode;
  logInPrompt?: ReactNode;
  onContinueInAppClick?: () => void;
  onContinueWithEmailClick: () => void;
  showAppCta?: boolean;
};

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=251%3A16532
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=250%3A16053

const Initial = ({ heading, logInPrompt, onContinueInAppClick, onContinueWithEmailClick, showAppCta = false, ...loginHandlers }: InitialProps) => {
  const ariaLabelledBy = useContext(ModalAriaLabelledByContext);

  return (
    <div className={styles.content}>
      <div className={styles.logo}>
        <Logo color="monochrome-dark" size="lg" variant="symbol" />
      </div>
      <Heading as="h4" className={classNames(styles.heading, styles.headingMaxWidth)} id={ariaLabelledBy} size="400">
        {heading || <FormattedMessage defaultMessage="To keep exploring trails, log in or sign up for free" />}
      </Heading>
      <AltLoginsForm {...loginHandlers} authType="signup" flatColors />
      <div className={styles.signUpLogInContainer}>
        <Button
          className={styles.email}
          text={<FormattedMessage defaultMessage="Continue with email" />}
          onClick={onContinueWithEmailClick}
          variant="secondary"
          width="full"
        />
        {showAppCta && onContinueInAppClick && (
          <>
            <SectionDivider classes={styles.divider}>
              <FormattedMessage defaultMessage="or" />
            </SectionDivider>
            <Button
              onClick={() => {
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'continue in app' });
                onContinueInAppClick?.();
              }}
              text={<FormattedMessage defaultMessage="Continue in app" />}
              variant="primary"
              width="full"
            />
          </>
        )}
      </div>
      {logInPrompt}
      <TermsOfService />
    </div>
  );
};

export default Initial;
